import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import HeaderNav from '../HeaderNav';
import CommunityFeed from './CommunityFeed';
import CreatePost from './CreatePost';
import CommunityFilters from './CommunityFilters';
import axios from 'axios';
import {
  UserGroupIcon,
  PlusIcon,
  FireIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

const PAGE_MOTION = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const Community = ({ user, onLogout }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [activeFilter, setActiveFilter] = useState('recent');
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);

  const filterOptions = [
    { id: 'recent', label: 'Recent', icon: ClockIcon },
    { id: 'trending', label: 'Trending', icon: ArrowTrendingUpIcon },
    { id: 'popular', label: 'Popular', icon: FireIcon },
  ];

  const topicOptions = [
    { id: 'all', label: 'All topics' },
    { id: 'investing', label: 'Investing' },
    { id: 'budgeting', label: 'Budgeting' },
    { id: 'goals', label: 'Financial goals' },
    { id: 'market', label: 'Market discussion' },
    { id: 'achievements', label: 'Achievements' },
    { id: 'advice', label: 'Advice & tips' },
  ];

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const topicParam = selectedTopic !== 'all' ? selectedTopic : null;
      const params = new URLSearchParams();
      params.append('filter_type', activeFilter);
      if (topicParam) params.append('topic', topicParam);

      const url = `/api/community/posts?${params.toString()}&_t=${Date.now()}`;
      const response = await axios.get(url);

      if (response.data.status === 'success' && response.data.data) {
        setPosts(response.data.data.posts || []);
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, selectedTopic]);

  useEffect(() => {
    fetchPosts();
  }, [activeFilter, selectedTopic, fetchPosts, refreshKey]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) fetchPosts();
    };
    const handleFocus = () => fetchPosts();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchPosts]);

  const handleCreatePost = async () => {
    setShowCreatePost(false);
    setRefreshKey((prev) => prev + 1);
    await fetchPosts();
  };

  return (
    <div className="min-h-screen bg-bone">
      <HeaderNav user={user} onLogout={onLogout} visionCount={0} />

      <div data-tour="aura-main" className="max-w-3xl mx-auto px-6 py-8">
        <motion.div {...PAGE_MOTION} className="mb-10">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg border border-hairline bg-bone-raised flex items-center justify-center">
                <UserGroupIcon className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <h1 className="font-display text-h1 text-primary">Community</h1>
                <p className="text-body text-secondary mt-1">
                  Connect, share, and learn with fellow investors
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowCreatePost(true)}
              className="btn-primary flex items-center gap-2 shrink-0"
            >
              <PlusIcon className="w-5 h-5" />
              <span>New post</span>
            </button>
          </div>
        </motion.div>

        <motion.div
          {...PAGE_MOTION}
          transition={{ ...PAGE_MOTION.transition, delay: 0.05 }}
          className="mb-8"
        >
          <CommunityFilters
            filterOptions={filterOptions}
            topicOptions={topicOptions}
            activeFilter={activeFilter}
            selectedTopic={selectedTopic}
            onFilterChange={setActiveFilter}
            onTopicChange={setSelectedTopic}
          />
        </motion.div>

        {showCreatePost && (
          <CreatePost
            user={user}
            topicOptions={topicOptions}
            onSubmit={handleCreatePost}
            onClose={() => setShowCreatePost(false)}
          />
        )}

        <motion.div
          {...PAGE_MOTION}
          transition={{ ...PAGE_MOTION.transition, delay: 0.1 }}
        >
          <CommunityFeed
            posts={posts}
            loading={loading}
            user={user}
            onPostUpdate={setPosts}
          />
        </motion.div>

        {!loading && posts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 border-t border-hairline mt-8"
          >
            <UserGroupIcon className="w-12 h-12 text-faint mx-auto mb-4" />
            <h3 className="font-display text-h2 text-primary mb-2">No posts yet</h3>
            <p className="text-body text-secondary mb-6 max-w-sm mx-auto">
              Be the first to start a conversation in the community.
            </p>
            <button type="button" onClick={() => setShowCreatePost(true)} className="btn-primary">
              Create first post
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Community;
